import React, { useEffect } from 'react';
import SEO from '../SEO';
import seoData from '../../seoData';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { CheckCircle, ArrowRight, Shield, Container, Droplets } from 'lucide-react';
import Header from '../Header';
import Footer from '../Footer';
import PageHero from '../ui/PageHero';
import { useNavigate } from 'react-router-dom';

const TankRepair = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <SEO title={seoData["services/tank-repair"]?.title} description={seoData["services/tank-repair"]?.description} path="/services/tank-repair" keywords={seoData["services/tank-repair"]?.keywords || []} />

      <PageHero
        badge="Core Services"
        title="Tank Coating & Water Tank Lining"
        description="Certified tank coating contractor and water tank lining specialist in California. Steel tank coating, water tank repair, elevated tank coating, ground storage tank coating, tank interior lining. NSF 61 potable water coatings. Fix corroded steel tanks and extend tank service life 15-25 years."
        backgroundKey="tank-repair"
        buttonText="Get In Touch"
      />

      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">Problems We Solve</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {['Interior tank corrosion and coating failure on water storage tanks',
              'Steel tank floor plate corrosion and product contamination',
              'Elevated tank interior and exterior coating degradation',
              'Chemical storage tank lining failure and chemical attack',
              'Concrete water tank leaks and rebar corrosion',
              'Secondary containment failures at tank farm areas'].map((challenge, index) => (
              <div key={index} className="flex items-start p-4 bg-cyan-50 rounded-lg">
                <Container className="text-cyan-600 mr-3 mt-0.5 flex-shrink-0" size={20} />
                <span className="text-gray-700 text-sm">{challenge}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">Our Solutions</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <Card className="border-l-4 border-l-cyan-600">
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <Droplets className="text-cyan-600 mr-3 flex-shrink-0" size={22} />
                  Potable Water Tank Lining
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 text-sm leading-relaxed">NSF/ANSI 61 certified coating systems for potable water storage. Steel and concrete water tank coating for ground storage, elevated tanks, standpipes, and reservoirs. Complete tank rehabilitation including surface prep, interior coating, and exterior finishing.</p>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-cyan-600">
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <Droplets className="text-cyan-600 mr-3 flex-shrink-0" size={22} />
                  Wastewater & Chemical Tank Lining
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 text-sm leading-relaxed">Chemical-resistant epoxy and vinyl ester lining systems for wastewater tanks, clarifiers, chemical storage, and process vessels. Immersion-grade coatings tested for your specific service fluids.</p>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-cyan-600">
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <Droplets className="text-cyan-600 mr-3 flex-shrink-0" size={22} />
                  Steel Tank Exterior Coating
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 text-sm leading-relaxed">Multi-coat atmospheric corrosion protection systems for tank exteriors, including zinc-rich primers, epoxy intermediates, and polyurethane topcoats. Color matching and logos for municipal and public-facing tanks.</p>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-cyan-600">
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <Droplets className="text-cyan-600 mr-3 flex-shrink-0" size={22} />
                  Secondary Containment Lining
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 text-sm leading-relaxed">Chemical-resistant containment coatings for dike areas, bund walls, and tank farm containment. Polyurea and epoxy systems that meet EPA SPCC and state environmental regulations.</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <div>
              <h2 className="text-3xl font-bold text-slate-900 mb-6">Why Choose BCS</h2>
              <div className="space-y-3">
                {['NSF/ANSI 61 certified systems for potable water contact',
                  'Tank coating contractor with 500+ tanks completed across California',
                  'Extend tank service life 15-25 years vs. costly tank replacement',
                  'Steel and concrete tank expertise — all tank types and sizes',
                  'Complete turnkey service — inspection, surface prep, coating, QA/QC',
                  'OSHA confined space certified crews for safe tank entry',
                  'On-site mobile blasting to SSPC-SP6/SP10 surface preparation standards',
                  'Full QA/QC: DFT, adhesion, holiday testing, and project documentation'].map((benefit, index) => (
                  <div key={index} className="flex items-start">
                    <CheckCircle className="text-green-700 mr-3 mt-0.5 flex-shrink-0" size={18} />
                    <span className="text-gray-700 text-sm">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-blue-900 text-white p-8 rounded-lg">
              <Shield className="mb-4" size={48} />
              <h3 className="text-xl font-bold mb-4">Belzona Products We Use</h3>
              <div className="space-y-3">
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                  <p className="font-semibold text-sm text-slate-900">Belzona 5811 (Immersion Grade)</p>
                  <p className="text-xs text-slate-500 mt-1">Potable water and immersion tank lining</p>
                </div>
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                  <p className="font-semibold text-sm text-slate-900">Belzona 1111 (Super Metal)</p>
                  <p className="text-xs text-slate-500 mt-1">Tank floor and shell corrosion repair</p>
                </div>
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                  <p className="font-semibold text-sm text-slate-900">Belzona 5831 (ST-Barrier)</p>
                  <p className="text-xs text-slate-500 mt-1">Surface-tolerant tank exterior coating</p>
                </div>
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                  <p className="font-semibold text-sm text-slate-900">Belzona 4111 (Magma-Quartz)</p>
                  <p className="text-xs text-slate-500 mt-1">Concrete tank repair and resurfacing</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-blue-900 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-4">Corroded Tank? We Restore It.</h2>
          <p className="text-blue-100 mb-8">Contact our tank coating specialists for a free tank inspection and coating recommendation. We serve water districts, municipalities, and industrial tank owners across California.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" className="bg-white text-blue-900 hover:bg-gray-100" onClick={() => window.location.href = 'tel:+19493719948'}>
              Call (949) 371-9948
            </Button>
            <Button size="lg" variant="outline" className="border-white text-white hover:bg-blue-800" onClick={() => navigate('/#contact')}>
              Request Assessment <ArrowRight className="ml-2" size={20} />
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default TankRepair;
